import React, { useState, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import {
  Upload, FileText, CheckCircle2, AlertTriangle, XCircle, Info, RefreshCw, PlayCircle,
} from 'lucide-react';
import { api, extractError } from '../lib/api';
import { fmtDate } from '../lib/date';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';

/**
 * TimeImportPage – Wöchentlicher Import von Zeitbuchungen aus CSV.
 *
 * Workflow:
 * 1. CSV auswählen → Backend liefert Preview mit Matching
 * 2. Übersicht in Ampellogik (OK / Irrelevant / Unbekannter MA / Unbekanntes Projekt)
 * 3. Checkboxen selektieren, dann "Übernehmen"
 */
export default function TimeImportPage() {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [filterText, setFilterText] = useState('');
  const [tab, setTab] = useState('ok');
  const [importResult, setImportResult] = useState(null);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setSelected(new Set());
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/import/time-csv/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
      // Preselect OK items
      const okIds = r.data.items.filter(i => i.status === 'ok').map(i => i.id);
      setSelected(new Set(okIds));
      if (okIds.length === 0) {
        toast.warning('Kein Match gefunden. Bitte prüfe die Übersicht.');
      } else {
        toast.success(`${okIds.length} zuweisbare Einträge gefunden.`);
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setUploading(false);
    }
  };

  const clearPreview = () => {
    setPreview(null);
    setSelected(new Set());
    if (fileRef.current) fileRef.current.value = '';
  };

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const items = preview?.items || [];
  const groupedByStatus = useMemo(() => {
    const buckets = { ok: [], irrelevant: [], unknown_project: [], unknown_employee: [] };
    for (const it of items) {
      if (buckets[it.status]) buckets[it.status].push(it);
    }
    return buckets;
  }, [items]);

  const filteredByTab = useMemo(() => {
    const list = groupedByStatus[tab] || [];
    if (!filterText.trim()) return list;
    const q = filterText.toLowerCase();
    return list.filter(i =>
      (i.employee_raw || '').toLowerCase().includes(q) ||
      (i.employee_name || '').toLowerCase().includes(q) ||
      (i.project_raw || '').toLowerCase().includes(q) ||
      (i.project_number || '').toLowerCase().includes(q) ||
      (i.project_name || '').toLowerCase().includes(q)
    );
  }, [groupedByStatus, tab, filterText]);

  const selectAllVisible = () => {
    const next = new Set(selected);
    for (const it of filteredByTab) {
      if (it.status === 'ok') next.add(it.id);
    }
    setSelected(next);
  };

  const unselectAllVisible = () => {
    const next = new Set(selected);
    for (const it of filteredByTab) next.delete(it.id);
    setSelected(next);
  };

  const applySelected = async () => {
    const toApply = items.filter(i => selected.has(i.id) && i.status === 'ok');
    if (toApply.length === 0) {
      toast.error('Keine OK-Einträge ausgewählt.');
      return;
    }
    setApplying(true);
    try {
      const payload = {
        items: toApply.map(i => ({
          employee_id: i.employee_id,
          project_id: i.project_id,
          date_iso: i.date_iso,
          hours: i.hours,
          existing_assignment_id: i.existing_assignment_id,
        })),
      };
      const r = await api.post('/import/time-csv/apply', payload);
      const dd = r.data;
      setImportResult(dd);
      const devSum = (dd.created_device || 0) + (dd.updated_device || 0);
      const normSum = (dd.created_normal || 0) + (dd.updated_normal || 0);
      toast.success(`Import fertig: ${normSum} Baustellen-Stunden, ${devSum} Geräte-Stunden verbucht`);
      clearPreview();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setApplying(false);
    }
  };

  const summary = preview?.summary;

  return (
    <div className="p-4 md:p-6 space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <Upload size={18}/>Zeitbuchungen importieren
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
            <FileText size={32} className="mx-auto text-slate-400 mb-2"/>
            <div className="text-sm text-slate-600 mb-3">
              CSV-Datei aus dem Zeiterfassungs-Tool auswählen (Format: <code className="text-xs bg-white px-1 rounded border">Mitarbeiter;Datum;Projekt;Stunden</code>).
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={e => uploadFile(e.target.files?.[0])}
              className="hidden"
              data-testid="time-import-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="time-import-upload-button"
            >
              {uploading ? <RefreshCw size={14} className="mr-2 animate-spin"/> : <Upload size={14} className="mr-2"/>}
              {uploading ? 'Analysiere…' : 'CSV wählen'}
            </Button>
            {preview && (
              <Button onClick={clearPreview} variant="outline" className="ml-2" data-testid="time-import-clear-button">
                Zurücksetzen
              </Button>
            )}
          </div>

          {preview && summary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <SummaryCard icon={<FileText size={14}/>} label="Zeilen gesamt" value={summary.total_rows}/>
                <SummaryCard icon={<Info size={14}/>} label="Aggregiert" value={summary.aggregated_rows}/>
                <SummaryCard icon={<CheckCircle2 size={14}/>} label="Übernehmbar" value={summary.ok} accent="emerald"/>
                <SummaryCard icon={<Info size={14}/>} label="Statistik-Ausschluss" value={summary.irrelevant} accent="slate"/>
                <SummaryCard icon={<AlertTriangle size={14}/>} label="Projekt fehlt" value={summary.unknown_project} accent="amber"/>
                <SummaryCard icon={<XCircle size={14}/>} label="MA unbekannt" value={summary.unknown_employee} accent="rose"/>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
                <div className="text-xs text-slate-600">
                  <strong className="font-mono">{summary.to_overwrite}</strong> bestehende Einsätze werden überschrieben.
                  {' '}<strong className="font-mono">{summary.to_create}</strong> Einsätze werden neu angelegt.
                  {preview.parse_errors?.length > 0 && (
                    <span className="ml-2 text-amber-700">
                      · {preview.parse_errors.length} Zeilen mit Parse-Fehlern übersprungen.
                    </span>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  <Badge className="bg-slate-900 text-white hover:bg-slate-900" data-testid="selected-count">{selected.size} ausgewählt</Badge>
                  <Button
                    onClick={applySelected}
                    disabled={applying || selected.size === 0}
                    data-testid="time-import-apply-button"
                  >
                    <PlayCircle size={14} className="mr-2"/>
                    {applying ? 'Übernehme…' : 'Ausgewählte übernehmen'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {importResult && (
        <Card data-testid="import-result-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-base">Import-Zusammenfassung</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                <div className="text-xs font-semibold text-sky-800 uppercase tracking-wide">Baustellen-Stunden (normale Plantafel)</div>
                <div className="mt-1 text-sm text-slate-700">
                  <span className="font-mono font-bold text-lg text-sky-900" data-testid="result-normal">{(importResult.created_normal || 0) + (importResult.updated_normal || 0)}</span> Einsätze
                  <span className="text-slate-500"> ({importResult.created_normal || 0} neu, {importResult.updated_normal || 0} aktualisiert)</span>
                </div>
              </div>
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                <div className="text-xs font-semibold text-orange-800 uppercase tracking-wide">Geräte-Stunden (Gerätefahrer)</div>
                <div className="mt-1 text-sm text-slate-700">
                  <span className="font-mono font-bold text-lg text-orange-900" data-testid="result-device">{(importResult.created_device || 0) + (importResult.updated_device || 0)}</span> Geräte-Einsätze
                  <span className="text-slate-500"> ({importResult.created_device || 0} neu, {importResult.updated_device || 0} aktualisiert)</span>
                </div>
              </div>
            </div>
            {importResult.skipped > 0 && (
              <p className="text-xs text-amber-700 mt-3">{importResult.skipped} Zeile(n) übersprungen{(importResult.errors || []).length ? `: ${importResult.errors.map(e => e.reason).slice(0, 5).join(' • ')}` : '.'}</p>
            )}
          </CardContent>
        </Card>
      )}

      {preview && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="font-display text-base">Preview & Auswahl</CardTitle>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Filter (Name/Projekt)…"
                  value={filterText}
                  onChange={e => setFilterText(e.target.value)}
                  className="w-56 h-8 text-xs"
                  data-testid="time-import-filter-input"
                />
                <Button size="sm" variant="outline" onClick={selectAllVisible} className="h-8 text-xs" data-testid="select-visible">
                  Alle sichtbaren wählen
                </Button>
                <Button size="sm" variant="outline" onClick={unselectAllVisible} className="h-8 text-xs" data-testid="unselect-visible">
                  Auswahl leeren
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="mb-3 flex-wrap h-auto">
                <TabsTrigger value="ok" data-testid="tab-ok" className="text-xs">
                  <CheckCircle2 size={12} className="mr-1 text-emerald-600"/>
                  Übernehmbar ({groupedByStatus.ok.length})
                </TabsTrigger>
                <TabsTrigger value="irrelevant" data-testid="tab-irrelevant" className="text-xs">
                  <Info size={12} className="mr-1 text-slate-500"/>
                  Statistik-Ausschluss ({groupedByStatus.irrelevant.length})
                </TabsTrigger>
                <TabsTrigger value="unknown_project" data-testid="tab-unknown-project" className="text-xs">
                  <AlertTriangle size={12} className="mr-1 text-amber-600"/>
                  Projekt fehlt ({groupedByStatus.unknown_project.length})
                </TabsTrigger>
                <TabsTrigger value="unknown_employee" data-testid="tab-unknown-employee" className="text-xs">
                  <XCircle size={12} className="mr-1 text-rose-600"/>
                  MA unbekannt ({groupedByStatus.unknown_employee.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value={tab}>
                <ImportTable
                  items={filteredByTab}
                  selected={selected}
                  onToggle={toggleOne}
                  tab={tab}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryCard({ icon, label, value, accent = 'slate' }) {
  const bg = {
    slate: 'bg-slate-50 border-slate-200 text-slate-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
  }[accent];
  return (
    <div className={`rounded-md border p-2.5 ${bg}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider">
        {icon}{label}
      </div>
      <div className="text-2xl font-bold font-mono mt-1 leading-none">{value}</div>
    </div>
  );
}

function ImportTable({ items, selected, onToggle, tab }) {
  if (items.length === 0) {
    return <div className="text-center text-sm text-slate-500 py-8">Keine Einträge in dieser Kategorie.</div>;
  }
  const canSelect = tab === 'ok';
  return (
    <div className="max-h-[500px] overflow-y-auto scroll-thin border rounded-md">
      <Table>
        <TableHeader className="sticky top-0 bg-white z-10">
          <TableRow>
            {canSelect && <TableHead className="w-8"></TableHead>}
            <TableHead>Mitarbeiter</TableHead>
            <TableHead>Datum</TableHead>
            <TableHead>Projekt</TableHead>
            <TableHead className="text-right">Stunden</TableHead>
            <TableHead>Detail</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map(item => (
            <TableRow key={item.id} data-testid={`import-row-${item.id}`}
                        className={item.existing_assignment_id ? 'bg-sky-50/50' : ''}>
              {canSelect && (
                <TableCell>
                  <Checkbox
                    checked={selected.has(item.id)}
                    onCheckedChange={() => onToggle(item.id)}
                    data-testid={`import-check-${item.id}`}
                  />
                </TableCell>
              )}
              <TableCell>
                <div className="font-semibold text-sm">{item.employee_name || item.employee_raw}</div>
                {!item.employee_id && <div className="text-[11px] text-rose-700">→ {item.employee_raw}</div>}
              </TableCell>
              <TableCell className="font-mono text-xs">{fmtDate(item.date_iso)}</TableCell>
              <TableCell>
                <div className="text-sm">
                  {item.project_number && <span className="font-mono text-[11px] font-bold mr-1">#{item.project_number}</span>}
                  {item.project_name || <span className="text-slate-500 italic">{item.project_raw || '(leer)'}</span>}
                </div>
              </TableCell>
              <TableCell className="text-right font-mono text-sm font-semibold">
                {(item.hours || 0).toString().replace('.', ',')}h
                {item.raw_hours_breakdown?.length > 1 && (
                  <div className="text-[10px] text-slate-500 font-normal">
                    aus {item.raw_hours_breakdown.length} Einträgen
                  </div>
                )}
              </TableCell>
              <TableCell className="text-xs text-slate-600">
                {item.existing_assignment_id && (
                  <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-800 border-sky-200 mr-1">
                    überschreibt {item.existing_hours}h
                  </Badge>
                )}
                {item.message && <span>{item.message}</span>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
